import React, { useEffect, useMemo, useState } from 'react';
import ProfaseModal from '../../../commons/ProfaseModal';
import { FaArrowLeft } from '@react-icons/all-files/fa/FaArrowLeft';
import { FaSave } from '@react-icons/all-files/fa/FaSave';
import PageForm, { FormField } from '../../../commons/PageForm';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  getProveedorConfigEnGrupo,
  resetProveedorConfig,
  updateProveedorConfigEnGrupo,
} from '../grupo-empresarial.reducer';
import { getEntities as getProveedoresCatalog } from '../../proveedor.reducer';
import ProveedorContadoresSection, {
  parseProveedorEmpresaContadores,
  type ProveedorContadorEntry,
} from './ProveedorContadoresSection';
import ProveedorContactosSection, {
  parseProveedorEmpresaContactos,
  type ProveedorContactoEntry,
} from './ProveedorContactosSection';
import {
  buildProveedorEmpresaConfigPayload,
  proveedorEmpresaConfigAlta,
  proveedorEmpresaConfigAlias,
  toDateInputValue,
} from './proveedor-config-preview';

const ACCENT = '#3b82f6';
const ASIGNAR_FORM_ID = 'grupo-proveedor-asignar-form';

export interface AsignarCompaniaFormValues {
  companiaId: string;
  alias: string;
  alta: string;
}

function makeEmptyForm(): AsignarCompaniaFormValues {
  return { companiaId: '', alias: '', alta: '' };
}

export interface GrupoProveedorAsignarModalProps {
  open: boolean;
  mode?: 'asignar' | 'edit';
  initialProveedorId?: number | null;
  grupoId: string | number | null;
  empresaId: number | null;
  empresaNombre?: string;
  onClose: () => void;
  onAssigned?: () => void;
}

export default function GrupoProveedorAsignarModal({
  open,
  mode = 'asignar',
  initialProveedorId = null,
  grupoId,
  empresaId,
  empresaNombre,
  onClose,
  onAssigned,
}: GrupoProveedorAsignarModalProps) {
  const isEdit = mode === 'edit' && initialProveedorId != null;
  const dispatch = useAppDispatch();
  const updating = useAppSelector(s => s.grupoEmpresarial.updating);
  const proveedorConfig = useAppSelector(s => s.grupoEmpresarial.proveedorConfig);
  const proveedorConfigLoading = useAppSelector(s => s.grupoEmpresarial.proveedorConfigLoading);
  const proveedorConfigProveedorId = useAppSelector(s => s.grupoEmpresarial.proveedorConfigProveedorId);

  const [form, setForm] = useState<AsignarCompaniaFormValues>(() => makeEmptyForm());
  const [errors, setErrors] = useState<Partial<Record<keyof AsignarCompaniaFormValues, string>>>({});
  const [catalogItems, setCatalogItems] = useState<{ id?: number; nombre?: string }[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [contadores, setContadores] = useState<ProveedorContadorEntry[]>([]);
  const [contactos, setContactos] = useState<ProveedorContactoEntry[]>([]);

  const companiaOptions = useMemo(
    () =>
      catalogItems
        .filter(p => p.id != null)
        .map(p => ({
          value: String(p.id),
          label: String(p.nombre ?? `ID ${p.id}`),
        }))
        .sort((a, b) => a.label.localeCompare(b.label, 'es')),
    [catalogItems],
  );

  const companiaSeleccionada = form.companiaId.trim() !== '';

  const formFields = useMemo<FormField<AsignarCompaniaFormValues>[]>(
    () => [
      {
        name: 'companiaId',
        label: 'Compañía',
        type: 'select',
        required: true,
        options: companiaOptions,
        disabled: isEdit,
      },
      {
        name: 'alias',
        label: 'Alias',
        type: 'text',
        disabled: !companiaSeleccionada,
      },
      {
        name: 'alta',
        label: 'Alta',
        type: 'date',
        disabled: !companiaSeleccionada,
      },
    ],
    [companiaOptions, companiaSeleccionada, isEdit],
  );

  const configParaSeleccion =
    companiaSeleccionada &&
    proveedorConfigProveedorId === Number(form.companiaId);

  useEffect(() => {
    if (!open) {
      dispatch(resetProveedorConfig());
      setCatalogItems([]);
      setCatalogLoading(false);
      return;
    }
    setErrors({});
    setContadores([]);
    setContactos([]);
    dispatch(resetProveedorConfig());

    if (isEdit && initialProveedorId != null) {
      setForm({
        companiaId: String(initialProveedorId),
        alias: '',
        alta: '',
      });
    } else {
      setForm(makeEmptyForm());
    }

    let cancelled = false;
    setCatalogLoading(true);
    dispatch(getProveedoresCatalog({ page: 0, size: 10000, sort: 'nombre,asc' }))
      .unwrap()
      .then(response => {
        if (cancelled) return;
        const raw = (response as { data?: unknown })?.data ?? response;
        setCatalogItems(Array.isArray(raw) ? raw : []);
      })
      .catch(() => {
        if (!cancelled) setCatalogItems([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, isEdit, initialProveedorId, dispatch]);

  useEffect(() => {
    if (!open || !isEdit || initialProveedorId == null) return;
    if (grupoId == null || grupoId === '' || empresaId == null) return;
    dispatch(
      getProveedorConfigEnGrupo({
        grupoId,
        empresaId,
        proveedorId: initialProveedorId,
      }),
    );
  }, [open, isEdit, initialProveedorId, grupoId, empresaId, dispatch]);

  useEffect(() => {
    if (!configParaSeleccion || proveedorConfigLoading) return;
    const altaApi = proveedorEmpresaConfigAlta(proveedorConfig);
    const aliasApi = proveedorEmpresaConfigAlias(proveedorConfig);
    const contadoresApi = parseProveedorEmpresaContadores(proveedorConfig);
    const contactosApi = parseProveedorEmpresaContactos(proveedorConfig);
    setForm(prev => {
      const next = { ...prev };
      if (altaApi) next.alta = toDateInputValue(altaApi);
      if (aliasApi) next.alias = aliasApi;
      return next;
    });
    setContadores(contadoresApi);
    setContactos(contactosApi);
  }, [configParaSeleccion, proveedorConfigLoading, proveedorConfig]);

  const fetchProveedorConfig = (proveedorId: number) => {
    if (grupoId == null || grupoId === '' || empresaId == null) return;
    dispatch(getProveedorConfigEnGrupo({ grupoId, empresaId, proveedorId }));
  };

  const handleFieldChange = (name: keyof AsignarCompaniaFormValues, value: string) => {
    setErrors(prev => ({ ...prev, [name]: undefined }));
    if (!companiaSeleccionada && name !== 'companiaId') return;

    if (name === 'companiaId') {
      if (value.trim() !== '') {
        const proveedorId = Number(value);
        setForm(prev => ({ ...prev, companiaId: value, alta: '', alias: '' }));
        setContadores([]);
        setContactos([]);
        if (Number.isFinite(proveedorId)) {
          fetchProveedorConfig(proveedorId);
        }
      } else {
        dispatch(resetProveedorConfig());
        setForm(prev => ({ ...prev, companiaId: '', alta: '', alias: '' }));
        setContadores([]);
        setContactos([]);
      }
      return;
    }
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (empresaId == null || grupoId == null || grupoId === '') return;

    const errs: Partial<Record<keyof AsignarCompaniaFormValues, string>> = {};
    if (!form.companiaId.trim()) {
      errs.companiaId = 'Seleccione una compañía';
    }
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    const proveedorId = Number(form.companiaId);
    if (!Number.isFinite(proveedorId)) {
      setErrors({ companiaId: 'Compañía no válida' });
      return;
    }

    const config = buildProveedorEmpresaConfigPayload({
      empresaId,
      proveedorId,
      alias: form.alias,
      alta: form.alta,
      contactos,
      contadores,
    });

    try {
      await dispatch(
        updateProveedorConfigEnGrupo({
          grupoId,
          empresaId,
          proveedorId,
          config,
        }),
      ).unwrap();
      onAssigned?.();
      onClose();
    } catch {
      /* 403 u otro error: interceptor / UI global */
    }
  };

  if (!open) return null;
  if (empresaId == null && !isEdit) return null;

  const subtitle = isEdit
    ? empresaNombre
      ? `Edite la compañía asignada a ${empresaNombre}.`
      : 'Edite la configuración de la compañía asignada.'
    : empresaNombre
      ? `Asigne una compañía a ${empresaNombre}.`
      : 'Seleccione la compañía a asignar a la empresa.';

  const catalogLoaded = !catalogLoading;
  const puedeMostrarForm = catalogLoaded && (companiaOptions.length > 0 || isEdit);
  const catalogoVacio = catalogLoaded && companiaOptions.length === 0 && !isEdit;
  const modalTitle = isEdit ? 'Editar Compañía' : 'Asignar Compañía';
  const submitLabel = isEdit ? 'Guardar' : 'Asignar';

  const proveedorNombre =
    configParaSeleccion && !proveedorConfigLoading
      ? proveedorConfig?.proveedor?.nombre?.trim()
      : '';

  return (
    <ProfaseModal
      open
      titleId="grupo-proveedor-asignar-modal-title"
      size="large"
      zIndex={100010}
      onBackdropMouseDown={e => {
        if (e.target === e.currentTarget && !updating) onClose();
      }}
    >
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <h2 id="grupo-proveedor-asignar-modal-title" className="profase-modal-title m-0">
          {modalTitle}
        </h2>
        <button
          type="button"
          className="profase-form-btn-secondary btn-sm flex-shrink-0"
          onClick={onClose}
          disabled={updating}
          aria-label="Cerrar"
        >
          Cerrar
        </button>
      </div>
      <p className="profase-modal-description">{subtitle}</p>
      {catalogLoading && (
        <p className="profase-form-muted-text" style={{ margin: '0 0 16px', fontSize: 14 }}>Cargando compañías…</p>
      )}
      {catalogoVacio && (
        <p className="profase-form-alert-warning" style={{ margin: '0 0 16px' }} role="status">
          No hay compañías disponibles para asignar.
        </p>
      )}
        {puedeMostrarForm && (
          <>
            {proveedorConfigLoading && configParaSeleccion ? (
              <p className="m-0 mb-2 text-muted" style={{ fontSize: 12 }}>
                Cargando configuración de la compañía…
              </p>
            ) : null}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0, 40%) minmax(0, 60%)',
                gap: 24,
                alignItems: 'start',
              }}
            >
              <div>
                {proveedorNombre ? (
                  <p className="m-0 mb-2" style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>
                    {proveedorNombre}
                  </p>
                ) : null}
                <PageForm
                  formId={ASIGNAR_FORM_ID}
                  fields={formFields}
                  values={form}
                  errors={errors}
                  isNew
                  saving={updating}
                  labelInline
                  hideActions
                  onFieldChange={handleFieldChange}
                  onSubmit={handleSubmit}
                  onCancel={onClose}
                  accentColor={ACCENT}
                  contentBeforeActions={
                    <ProveedorContadoresSection
                      contadores={contadores}
                      onChange={setContadores}
                      disabled={!companiaSeleccionada}
                    />
                  }
                />
              </div>
              <ProveedorContactosSection
                grupoId={grupoId}
                contactos={contactos}
                onChange={setContactos}
                disabled={!companiaSeleccionada}
              />
            </div>
            <div
              className="profase-form-actions"
              style={{
                paddingTop: 16,
                marginTop: 8,
              }}
            >
              <button
                type="button"
                className="profase-form-btn-secondary d-inline-flex align-items-center"
                style={{ padding: '9px 20px', fontSize: 13, fontWeight: 600 }}
                onClick={onClose}
                disabled={updating}
              >
                <FaArrowLeft style={{ marginRight: 6 }} />
                Cancelar
              </button>
              <button
                type="submit"
                form={ASIGNAR_FORM_ID}
                className="btn d-inline-flex align-items-center text-white border-0"
                style={{ padding: '9px 20px', fontSize: 13, fontWeight: 600, background: ACCENT }}
                disabled={updating}
              >
                <FaSave style={{ marginRight: 6 }} />
                {updating ? 'Guardando…' : submitLabel}
              </button>
            </div>
          </>
        )}
    </ProfaseModal>
  );
}
